import { Button, Icon, SingleSelectInput, Spinner, Text, TextInput } from "@ucl/react-library";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useBoundInput } from "src/hooks/use-bound-input";

interface AddressLookupProps {
  addressOptions: { value: string; label: string; }[];
  onSelectedAddressChange: (value: { value: string; label: string; }) => void;
  toggleAddressType: () => void;
  errorMessage?: string;
  isFetching?: boolean;
}

export const AddressLookup = ({ addressOptions, onSelectedAddressChange, toggleAddressType, errorMessage, isFetching }: AddressLookupProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "editContactDetails" });
  const { setValue, watch } = useFormContext();
  const postcodeLookupProps = useBoundInput({ name: "address.postcodeLookup" });
  const addressSelectProps = useBoundInput({ name: "address.addressSelector" });

  const address = watch("address");

  const backToLookup = () => {
    setValue("address.addressSelector", "");
  };

  return (<div data-testid="addressLookupSection">
    {addressSelectProps.value ?
      <div className="flex flex-col p-5 gap-5 bg-gray-100 rounded-xl" data-testid="selectedAddressView">
        <Text.P size="large" fontWeight="semi-bold">{t("address")}</Text.P>
        <div>
          <Text.P size="large">{address.addressLine1}</Text.P>
          <Text.P size="large">{address.addressLine2}</Text.P>
          <Text.P size="large">{address.city}</Text.P>
          <Text.P size="large">{address.county}</Text.P>
          <Text.P size="large">{address.postCode}</Text.P>
          <Text.P size="large">{address.country}</Text.P>
        </div>
        <Button type="button" variant="tertiary" onClick={backToLookup} fullWidthMobile={false} className="self-start !pl-0 !underline !text-primary">{t("changeAddress")}</Button>
      </div> :

      <div className="flex flex-col gap-5">
        <TextInput
          data-testid="postcodeLookup"
          labelProp={{ label: t("enterPostcode") }}
          placeholder=""
          {...postcodeLookupProps}
          helperTextProp={{ secondaryProp: { helperText: errorMessage } }}
          inputState={errorMessage ? "error" : "none"}
          startAdornmentProp={{ startAdornment: <Icon icon="search" /> }}
          endAdornmentProp={isFetching ? { endAdornment: <Spinner size="small" aria-label="loading" /> } : {}}
        />
        <div>
          {addressOptions.length > 0 && !isFetching && <SingleSelectInput
            labelProp={{ label: t("selectAddress") }}
            name={addressSelectProps.name}
            ref={addressSelectProps.ref}
            onBlur={addressSelectProps.onBlur}
            data-testid={"addressSelector"}
            options={addressOptions}
            selected={addressSelectProps.value}
            onOptionSelect={(option) => {
              addressSelectProps.onChange(option.value);
              onSelectedAddressChange(option);
            }}
          />}
          <Button type="button" variant="tertiary" onClick={toggleAddressType} fullWidthMobile={false} className="!pl-0 !underline !text-primary">{t("enterManually")}</Button>
        </div>
      </div>}
  </div>
  );
};